<template>
    <v-container>
        <v-card class="pa-4">
            <v-card-title>
                <div class="d-flex align-center">
                    <v-btn icon @click="goBack" class="mr-2">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-chip color="info" text-color="white" small>
                        Актуальные тарифы
                    </v-chip>
                </div>
            </v-card-title>

            <v-card-text>
                <v-data-table dense :items="latestTariffs" :headers="headers" :loading="loading"
                    :options.sync="paginationOptions" :server-items-length="totalCount" :footer-props="{
                        'items-per-page-options': [5, 10, 25, 50, 100],
                        'show-current-page': true,
                        'show-first-last-page': true
                    }" item-key="id" loading-text="Загрузка данных..." no-data-text="Нет параметров биллинга"
                    class="styled-table pointed-table" @update:options="onPaginationChange">
                    <!-- Вид -->
                    <template v-slot:[`item.vid`]="{ item }">
                        {{ item.vid }}
                    </template>

                    <!-- Наименование -->
                    <template v-slot:[`item.name`]="{ item }">
                        {{ item.name }}
                    </template>

                    <!-- Тариф с форматированием -->
                    <template v-slot:[`item.value`]="{ item }">
                        {{ formatCurrency(item.value) }}
                    </template>

                    <!-- Дата начала -->
                    <template v-slot:[`item.dateBegin`]="{ item }">
                        {{ formatDate(item.dateBegin) }}
                    </template>

                    <!-- Статус (если есть будущие тарифы) -->
                    <template v-slot:[`item.hasFuture`]="{ item }">
                        <v-chip v-if="item.hasFutureTariffs" small color="warning" text-color="white">
                            Есть будущие
                        </v-chip>
                        <span v-else>—</span>
                    </template>

                    <!-- Actions column -->
                    <template v-slot:[`item.actions`]="{ item }">
                        <div class="d-flex justify-space-around">
                            <v-tooltip v-if="canEdit" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon color="primary" v-bind="attrs" v-on="on"
                                        @click="editBillingParameter(item)">
                                        mdi-pencil
                                    </v-icon>
                                </template>
                                <span>Изменить</span>
                            </v-tooltip>

                            <v-tooltip v-if="canDelete" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon color="red" v-bind="attrs" v-on="on" @click="confirmDelete(item)">
                                        mdi-delete
                                    </v-icon>
                                </template>
                                <span>Удалить</span>
                            </v-tooltip>

                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon color="info" v-bind="attrs" v-on="on" @click.stop="viewHistory(item)">
                                        mdi-history
                                    </v-icon>
                                </template>
                                <span>История изменений</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <!-- Loading indicator -->
                    <template v-slot:progress>
                        <v-progress-linear indeterminate color="primary" />
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Диалог подтверждения удаления -->
        <v-dialog v-model="deleteDialog" max-width="400">
            <v-card>
                <v-card-title class="headline">Подтверждение удаления</v-card-title>
                <v-card-text>
                    Вы действительно хотите удалить тариф "{{ itemToDelete?.name }}"?
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="deleteDialog = false">Отмена</v-btn>
                    <v-btn color="red" text @click="deleteBillingParameter" :loading="deleting">
                        Удалить
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-sheet height="64" />
        <v-btn v-if="canAdd" bottom right color="primary" dark fab fixed @click="addBillingParameter">
            <v-icon class="white--text">
                mdi-plus
            </v-icon>
        </v-btn>
    </v-container>
</template>

<script>
export default {
    name: 'BillingParameters',

    data() {
        return {
            cargoTypeID: null,
            loading: false,
            deleting: false,
            error: null,
            deleteDialog: false,
            itemToDelete: null,
            allTariffs: [], // Все тарифы (включая историю)
            totalCount: 0,
            paginationOptions: {
                page: 1,
                itemsPerPage: 10,
                sortBy: ['name'],
                sortDesc: [false]
            },
            headers: [
                { text: 'Вид', value: 'vid', sortable: true },
                { text: 'Наименование', value: 'name', sortable: true },
                { text: 'Тариф', value: 'value', align: 'right', sortable: true },
                { text: 'Дата начала', value: 'dateBegin', sortable: true },
                { text: 'Будущие тарифы', value: 'hasFuture', sortable: false, align: 'center' },
                { text: 'Действия', value: 'actions', sortable: false, align: 'center', width: '150px' }
            ]
        };
    },

    computed: {
        // Только последние (актуальные) тарифы
        latestTariffs() {
            if (!this.allTariffs.length) return [];

            // Группируем по idColumn с помощью reduce
            const groupedByColumn = this.allTariffs.reduce((groups, tariff) => {
                const columnId = tariff.idColumn;
                if (!groups[columnId]) {
                    groups[columnId] = [];
                }
                groups[columnId].push(tariff);
                return groups;
            }, {});

            const latestItems = [];

            for (const [columnId, tariffs] of Object.entries(groupedByColumn)) {
                // Сортируем по дате начала (сначала новые)
                const sortedTariffs = [...tariffs].sort((a, b) =>
                    new Date(b.dateBegin) - new Date(a.dateBegin)
                );

                if (sortedTariffs.length > 0) {
                    // Берем самый последний тариф
                    const latest = sortedTariffs[0];

                    // Проверяем, есть ли будущие тарифы (дата начала > текущей даты)
                    const hasFutureTariffs = sortedTariffs.some(t =>
                        new Date(t.dateBegin) > new Date()
                    );

                    latestItems.push({
                        ...latest,
                        hasFutureTariffs
                    });
                }
            }

            // Применяем сортировку из paginationOptions
            const sortedItems = this.applySorting(latestItems);

            // Применяем пагинацию
            const start = (this.paginationOptions.page - 1) * this.paginationOptions.itemsPerPage;
            const end = start + this.paginationOptions.itemsPerPage;

            return sortedItems.slice(start, end);
        },

        // Permission checks
        canAdd() {
            return this.$store?.state?.auth?.user?.role === "Управляющий" ||
                this.$store?.state?.auth?.user?.role === "Администратор";
        },
        canDelete() {
            return this.$store?.state?.auth?.user?.role === "Управляющий" ||
                this.$store?.state?.auth?.user?.role === "Администратор";
        },
        canEdit() {
            return this.$store?.state?.auth?.user?.role === "Управляющий" ||
                this.$store?.state?.auth?.user?.role === "Администратор";
        }
    },

    watch: {
        // Следим за изменениями allTariffs для обновления totalCount
        allTariffs: {
            handler() {
                this.updateTotalCount();
            },
            deep: true
        }
    },

    async created() {
        await this.loadData();
    },

    methods: {
        async loadData() {
            this.loading = true;
            try {
                this.cargoTypeID = this.$route.params.cargoTypeID;

                // Загружаем все данные (без пагинации на сервере, так как нужна группировка)
                const response = await this.axios.get(
                    `/billing/bill-tarif-details/${this.cargoTypeID}/1/1000` // Загружаем все записи
                );

                // Handle response
                if (response.data.items) {
                    this.transformApiData(response.data.items);
                    this.totalCount = response.data.totalCount;
                } else {
                    this.transformApiData(response.data);
                }

            } catch (error) {
                console.error('Error loading cargo type:', error);
                this.showError('Не удалось загрузить данные');
            } finally {
                this.loading = false;
            }
        },

        onPaginationChange(options) {
            this.paginationOptions = options;
        },

        applySorting(items) {
            if (!this.paginationOptions.sortBy.length) return items;

            const sortBy = this.paginationOptions.sortBy[0];
            const sortDesc = this.paginationOptions.sortDesc[0];

            return [...items].sort((a, b) => {
                let aValue = a[sortBy];
                let bValue = b[sortBy];

                // Handle different value types
                if (sortBy === 'value') {
                    aValue = parseFloat(aValue) || 0;
                    bValue = parseFloat(bValue) || 0;
                } else if (sortBy === 'dateBegin') {
                    aValue = new Date(aValue).getTime();
                    bValue = new Date(bValue).getTime();
                } else {
                    aValue = String(aValue || '').toLowerCase();
                    bValue = String(bValue || '').toLowerCase();
                }

                if (aValue < bValue) return sortDesc ? 1 : -1;
                if (aValue > bValue) return sortDesc ? -1 : 1;
                return 0;
            });
        },

        updateTotalCount() {
            // Вычисляем количество уникальных колонок (после группировки)
            const uniqueColumns = new Set(this.allTariffs.map(t => t.idColumn));
            this.totalCount = uniqueColumns.size;
        },

        transformApiData(apiData) {
            if (!apiData || !Array.isArray(apiData)) {
                this.allTariffs = [];
                return;
            }

            // Фильтруем только неудаленные записи
            const activeItems = apiData.filter(item =>
                !item.isDeleted &&
                item.billColumn &&
                !item.billColumn.isDeleted
            );

            // Трансформируем данные
            this.allTariffs = activeItems.map(item => ({
                id: item.id,
                idColumn: item.billColumn?.id,
                vid: item.billColumn?.billColumnVid?.name || 'Не указано',
                name: item.billColumn?.name || 'Не указано',
                value: item.value,
                dateBegin: item.dateBegin,
                originalData: item,
                cargoTypeID: this.cargoTypeID
            }));

            this.updateTotalCount();
        },

        formatCurrency(value) {
            if (value === null || value === undefined) return '0.00';
            return new Intl.NumberFormat('ru-RU', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        },

        formatDate(dateString) {
            if (!dateString) return '';

            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            return `${day}.${month}.${year}`;
        },

        goBack() {
            this.$router.push('/settings/cargoTypes');
        },

        confirmDelete(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
        },

        async deleteBillingParameter() {
            if (!this.itemToDelete) return;

            this.deleting = true;

            try {
                await this.axios.delete(`/Billing/bill-tarif-detal/${this.itemToDelete.id}`);
                this.showSuccess('Тариф успешно удален');
                this.deleteDialog = false;
                await this.loadData(); // Перезагружаем данные
            } catch (error) {
                console.error('Error deleting billing parameter:', error);
                this.handleError(error);
            } finally {
                this.deleting = false;
                this.itemToDelete = null;
            }
        },

        editBillingParameter(item) {
            this.$router.push(`/settings/cargoTypes/BillingParameters/update/${item.id}`);
        },

        addBillingParameter() {
            this.$router.push({
                name: 'AddBillingParameter',
                params: {
                    cargoTypeID: this.cargoTypeID
                }
            });
        },

        viewHistory(item) {
            this.$router.push({
                name: 'BillParameterHistory',
                params: {
                    idColumn: item.idColumn,
                    cargoTypeId: this.cargoTypeID,
                    columnName: item.name
                }
            });
        },

        handleError(error) {
            if (error.response) {
                if (error.response.status === 404) {
                    this.showError('Запись не найдена');
                } else if (error.response.status === 401) {
                    this.showError('Необходима авторизация');
                } else {
                    this.showError(`Ошибка сервера: ${error.response.status}`);
                }
            } else if (error.request) {
                this.showError('Сервер не отвечает');
            } else {
                this.showError('Ошибка при выполнении операции');
            }
        },

        showSuccess(message) {
            if (this.$toast?.success) {
                this.$toast.success(message);
            } else {
                console.log('Success:', message);
            }
        },

        showError(message) {
            if (this.$toast?.error) {
                this.$toast.error(message);
            } else {
                console.error('Error:', message);
            }
        }
    }
};
</script>

<style scoped>
.styled-table :deep(.v-data-table__wrapper tbody tr:hover) {
    background-color: #f5f5f5 !important;
    cursor: pointer;
}

.v-data-table :deep(.v-data-table__progress) {
    height: 2px !important;
}
</style>